/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListDeploymentsRequest,
  ListDeploymentsSecurity,
} from "../models/operations/listdeployments.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListDeploymentsQuery,
  ListDeploymentsQueryData,
  prefetchListDeployments,
  queryKeyListDeployments,
} from "./listDeployments.core.js";
export {
  buildListDeploymentsQuery,
  type ListDeploymentsQueryData,
  prefetchListDeployments,
  queryKeyListDeployments,
};

export type ListDeploymentsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listDeployments deployments
 *
 * @remarks
 * List all deployments in descending order of creation.
 */
export function useListDeployments(
  request?: ListDeploymentsRequest | undefined,
  security?: ListDeploymentsSecurity | undefined,
  options?: QueryHookOptions<
    ListDeploymentsQueryData,
    ListDeploymentsQueryError
  >,
): UseQueryResult<ListDeploymentsQueryData, ListDeploymentsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildListDeploymentsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listDeployments deployments
 *
 * @remarks
 * List all deployments in descending order of creation.
 */
export function useListDeploymentsSuspense(
  request?: ListDeploymentsRequest | undefined,
  security?: ListDeploymentsSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListDeploymentsQueryData,
    ListDeploymentsQueryError
  >,
): UseSuspenseQueryResult<ListDeploymentsQueryData, ListDeploymentsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListDeploymentsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListDeploymentsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: ListDeploymentsQueryData,
): ListDeploymentsQueryData | undefined {
  const key = queryKeyListDeployments(...queryKeyBase);

  return client.setQueryData<ListDeploymentsQueryData>(key, data);
}

export function invalidateListDeployments(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      gramKey?: string | undefined;
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "deployments", "list", ...queryKeyBase],
  });
}

export function invalidateAllListDeployments(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "deployments", "list"],
  });
}
